import { defineStore } from "pinia";
import { computed, del, ref, set } from "vue";
import { isHDCA } from "@/api";
import { fetchCollectionDetails, fetchElementsFromCollection } from "@/api/datasetCollections";
import { ensureDefined } from "@/utils/assertions";
import { ActionSkippedError, LastQueue } from "@/utils/lastQueue";
const FETCH_LIMIT = 50;
export const useCollectionElementsStore = defineStore("collectionElementsStore", () => {
    const storedCollections = ref({});
    const loadingCollectionElements = ref({});
    const loadingCollectionElementsErrors = ref({});
    const storedCollectionElements = ref({});
    /**
     * Returns a key that can be used to store or retrieve the elements of a collection in the store.
     *
     * It consistently returns a DatasetCollection ID for (top level) HDCAs or sub-collections.
     */
    function getCollectionKey(collection) {
        if (isHDCA(collection)) {
            return collection.collection_id;
        }
        return collection.id;
    }
    const getCollectionElements = computed(() => {
        return (collection) => {
            return storedCollectionElements.value[getCollectionKey(collection)];
        };
    });
    const isLoadingCollectionElements = computed(() => {
        return (collection) => {
            return loadingCollectionElements.value[getCollectionKey(collection)] ?? false;
        };
    });
    const getLoadingCollectionElementsError = computed(() => {
        return (collection) => {
            return loadingCollectionElementsErrors.value[getCollectionKey(collection)] ?? false;
        };
    });
    async function fetchMissing({ storedElements, collection, offset, limit = FETCH_LIMIT }) {
        const collectionKey = getCollectionKey(collection);
        try {
            if (collection.element_count !== null) {
                // We should fetch only missing (placeholder) elements from the range
                const firstMissingIndexInRange = storedElements
                    .slice(offset, offset + limit)
                    .findIndex((element) => (isPlaceholder(element) && !element.fetching) || isInvalid(element));
                if (firstMissingIndexInRange === -1) {
                    // All elements in the range are already stored or being fetched
                    return;
                }
                // Adjust the offset to the first missing element
                offset += firstMissingIndexInRange;
            }
            else {
                // Edge case where element_count is incorrect
                // TODO: remove me once element_count is reported reliably
                offset = 0;
            }
            set(loadingCollectionElements.value, collectionKey, true);
            // Mark all elements in the range as fetching
            storedElements
                .slice(offset, offset + limit)
                .forEach((element) => isPlaceholder(element) && (element.fetching = true));
            const fetchedElements = await fetchElementsFromCollection({
                entry: collection,
                offset: offset,
                limit: limit,
            });
            return { fetchedElements, elementOffset: offset };
        }
        catch (error) {
            set(loadingCollectionElementsErrors.value, collectionKey, error);
        }
        finally {
            del(loadingCollectionElements.value, collectionKey);
        }
    }
    const lastQueue = new LastQueue(1000, true);
    async function fetchMissingElements(collection, offset, limit = FETCH_LIMIT) {
        const key = getCollectionKey(collection);
        let storedElements = storedCollectionElements.value[key];
        if (!storedElements) {
            storedElements = initWithPlaceholderElements(collection);
            set(storedCollectionElements.value, key, storedElements);
        }
        try {
            const data = await lastQueue.enqueue(fetchMissing, { storedElements, collection, offset, limit }, key);
            if (data) {
                const from = data.elementOffset;
                const to = from + data.fetchedElements.length;
                for (let index = from; index < to; index++) {
                    const element = ensureDefined(data.fetchedElements[index - from]);
                    set(storedElements, index, element);
                }
                set(storedCollectionElements.value, key, storedElements);
            }
        }
        catch (e) {
            if (!(e instanceof ActionSkippedError)) {
                throw e;
            }
        }
    }
    async function invalidateCollectionElements(collection) {
        const storedElements = storedCollectionElements.value[getCollectionKey(collection)] ?? [];
        storedElements.forEach((element) => {
            element.valid = false;
        });
    }
    function saveCollections(historyContentsPayload) {
        const collectionsInHistory = historyContentsPayload.filter((entry) => entry.history_content_type === "dataset_collection");
        for (const collection of collectionsInHistory) {
            set(storedCollections.value, collection.id, collection);
        }
    }
    /** Returns collection from storedCollections, will load collection if not in store */
    const getCollectionById = computed(() => {
        return (collectionId) => {
            if (!storedCollections.value[collectionId] && !loadingCollectionElementsErrors.value[collectionId]) {
                // TODO: Try to remove this as it can cause computed side effects (use keyedCache in this store instead?)
                fetchCollection({ id: collectionId });
            }
            return storedCollections.value[collectionId] ?? null;
        };
    });
    async function fetchCollection(params) {
        set(loadingCollectionElements.value, params.id, true);
        try {
            const collection = await fetchCollectionDetails({ id: params.id });
            set(storedCollections.value, collection.id, collection);
            return collection;
        }
        catch (error) {
            set(loadingCollectionElementsErrors.value, params.id, error);
        }
        finally {
            del(loadingCollectionElements.value, params.id);
        }
    }
    function isPlaceholder(element) {
        return "id" in element === false;
    }
    function isInvalid(element) {
        return element["valid"] === false;
    }
    function initWithPlaceholderElements(collection) {
        const totalElements = collection.element_count ?? 0;
        const placeholderElements = new Array(totalElements);
        for (let i = 0; i < totalElements; i++) {
            placeholderElements[i] = { element_index: i };
        }
        return placeholderElements;
    }
    return {
        storedCollections,
        storedCollectionElements,
        getCollectionElements,
        isLoadingCollectionElements,
        getLoadingCollectionElementsError,
        loadingCollectionElementsErrors,
        getCollectionById,
        fetchCollection,
        invalidateCollectionElements,
        saveCollections,
        getCollectionKey,
        fetchMissingElements,
    };
});
